import { usePortalRole, type PortalRole, ADMIN_VIEW_AS_ROLES } from "@/hooks/usePortalRole";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  LayoutDashboard,
  UserCog,
  HardHat,
  Users,
  Handshake,
  BarChart3,
  DoorOpen,
  TrendingUp,
  Headphones,
  ClipboardList,
  Eye,
} from "lucide-react";

const roleConfig: Record<Exclude<PortalRole, null>, { label: string; icon: typeof LayoutDashboard; badge: string; home: string }> = {
  admin: { label: "Owner", icon: LayoutDashboard, badge: "bg-destructive/10 text-destructive", home: "/portal/admin" },
  consultant: { label: "Consultant", icon: UserCog, badge: "bg-primary/10 text-primary", home: "/portal/consultant" },
  installer: { label: "Installer", icon: HardHat, badge: "bg-accent/10 text-accent", home: "/portal/installer" },
  customer: { label: "Customer", icon: Users, badge: "bg-success-muted text-success-text", home: "/customer" },
  partner: { label: "Partner", icon: Handshake, badge: "bg-info-muted text-info-text", home: "/portal/partner" },
  marketing_manager: { label: "Marketing", icon: BarChart3, badge: "bg-accent/10 text-accent", home: "/portal/marketing" },
  introducer: { label: "Introducer", icon: DoorOpen, badge: "bg-warning-muted text-warning-text", home: "/portal/introducer" },
  introducer_manager: { label: "Introducer Manager", icon: DoorOpen, badge: "bg-warning-muted text-warning-text", home: "/portal/introducer-manager" },
  sales_manager: { label: "Sales", icon: TrendingUp, badge: "bg-primary/10 text-primary", home: "/portal/sales" },
  customer_service: { label: "Customer Service", icon: Headphones, badge: "bg-info-muted text-info-text", home: "/portal/customer-service" },
  surveyor: { label: "Surveyor", icon: ClipboardList, badge: "bg-primary/10 text-primary", home: "/portal/surveyor" },
};

export default function RoleSwitcher() {
  const { role, roles, setActiveRole, isViewAs } = usePortalRole();
  const navigate = useNavigate();

  const isAdmin = roles.includes("admin");

  // Hide entirely for single-role non-admin users
  if (!role || (!isAdmin && roles.length < 2)) return null;

  const current = roleConfig[role];
  if (!current) return null;

  const handleSwitch = (newRole: PortalRole) => {
    if (!newRole || newRole === role) return;
    setActiveRole(newRole);
    const config = roleConfig[newRole];
    if (config) navigate(config.home);
  };

  // Roles the admin can "view as" but does not actually hold
  const viewAsOnly = isAdmin
    ? ADMIN_VIEW_AS_ROLES.filter((r) => r !== "admin")
    : [];

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <DropdownMenu>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`gap-1.5 text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full min-h-[44px] ${current.badge}`}
                aria-label={`Viewing as ${current.label}. Switch workspace.`}
              >
                <current.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{isViewAs ? "Preview:" : "Workspace:"} {current.label}</span>
                <span className="sm:hidden">{current.label}</span>
                <ChevronDown className="w-3 h-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isAdmin
              ? "Open a role preview. Your owner access still applies; use a separate account to test permissions."
              : "Switch which workspace you see. Your permissions do not change."}
          </TooltipContent>
          <DropdownMenuContent align="center" className="min-w-[220px]">
            {roles.length > 0 && (
              <>
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {isAdmin ? "Your workspace" : "Your roles"}
                </DropdownMenuLabel>
                {roles.filter((r) => !isAdmin || r === "admin" || r === "customer").map((r) => {
                  if (!r) return null;
                  const cfg = roleConfig[r];
                  if (!cfg) return null;
                  const isActive = r === role && !isViewAs;
                  return (
                    <DropdownMenuItem
                      key={r}
                      onClick={() => handleSwitch(r)}
                      className={`gap-2.5 text-sm ${isActive ? "font-semibold bg-muted" : ""}`}
                    >
                      <cfg.icon className="w-4 h-4 shrink-0" />
                      {isAdmin && r === "customer" ? "My Home Hub" : cfg.label}
                      {isActive && (
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">Active</span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}

            {viewAsOnly.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <Eye className="w-3 h-3" /> Preview staff & partner views
                </DropdownMenuLabel>
                {viewAsOnly.map((r) => {
                  const cfg = roleConfig[r];
                  if (!cfg) return null;
                  const isActive = r === role && isViewAs;
                  return (
                    <DropdownMenuItem
                      key={`viewas-${r}`}
                      onClick={() => handleSwitch(r)}
                      className={`gap-2.5 text-sm ${isActive ? "font-semibold bg-muted" : ""}`}
                    >
                      <cfg.icon className="w-4 h-4 shrink-0" />
                      {isAdmin && r === "customer" ? "My Home Hub" : cfg.label}
                      {isActive && (
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">Active</span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  );
}
